import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Typography, Tabs, Tab, IconButton, Divider, Grid, Button } from '@mui/material'
import { Add as AddIcon, FilterList as FilterListIcon, MoreVert as MoreVertIcon } from '@mui/icons-material'
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid'
import { common } from '@mui/material/colors'
import { get } from 'utils/lodash'
import { toast } from 'react-toastify'

import T from 'T'
import StyledBadge from 'components/locations/filters/StyledBadge'
import {
  setSelectedTab,
  setExpandedRowIds,
  setIsOpenAddLocationDialog,
  setMoreActionsAnchorEl,
  setShowDeactivateAllConfirmation,
  resetFilters,
} from 'slices/customer/customerDetailsLocationsSlice'
import {
  selectSelectedTab,
  selectActiveFiltersCount,
  selectShowDeactivateAllConfirmation,
} from 'data/customer/customerDetailsLocationsSelectors'
import { getActiveDeactivatedLocations } from 'utils/location'
import { useActivateDeactivateLocationsMutation } from 'api/location/activateDeactivateLocations'
import { handleError } from 'utils/error'
import { ACTIVE, DEACTIVATED } from '../settings'
import LocationSortPopover from './LocationSortPopover'
import DeactivateAllConfirmation from './DeactivateAllConfirmation'

const LocationsDataGridHeader = ({
  customerDetails,
  showAddLocationIcon,
  onFilterButtonClick,
  onLocationStatusChange,
  handleResetLocations,
  onTabChange,
}) => {
  const selectedTab = useSelector(selectSelectedTab)
  const activeFiltersCount = useSelector(selectActiveFiltersCount)
  const showDeactivateAllConfirmation = useSelector(selectShowDeactivateAllConfirmation)
  const dispatch = useDispatch()
  const [activateDeactivateLocations] = useActivateDeactivateLocationsMutation()
  const { activeLocations, deactivatedLocations } = getActiveDeactivatedLocations(customerDetails, true)
  const totalLocationsCount = activeLocations.length + deactivatedLocations.length
  const deactivatableLocations = useMemo(() => {
    return activeLocations.filter(location => get(location, 'serviceCount', 0) === 0)
  }, [activeLocations])

  const handleTabChange = (event, newValue) => {
    dispatch(setSelectedTab(newValue))
    dispatch(setExpandedRowIds([]))
    onTabChange(newValue)
  }

  const handleAddLocationClick = () => {
    dispatch(setIsOpenAddLocationDialog(true))
  }

  const handleMoreActionsClick = event => {
    dispatch(setMoreActionsAnchorEl(event.currentTarget))
  }

  const handleConfirmDeactivateAll = () => {
    if (deactivatableLocations.length === 0) return

    const payload = {
      deactivate: true,
      locations: deactivatableLocations.map(location => ({ id: location.id })),
    }

    activateDeactivateLocations(payload)
      .unwrap()
      .then(() => {
        dispatch(setShowDeactivateAllConfirmation(false))
        if (onLocationStatusChange) {
          onLocationStatusChange()
        }
        toast.success(
          deactivatableLocations.length === 1
            ? T.LOCATION_DEACTIVATED_MSG
            : `${deactivatableLocations.length} locations deactivated successfully`
        )
      })
      .catch(error => {
        handleError(error)
        dispatch(setShowDeactivateAllConfirmation(false))
      })
  }

  const handleCancelDeactivateAll = () => {
    dispatch(setShowDeactivateAllConfirmation(false))
  }

  const handleAllLocationsClick = () => {
    dispatch(resetFilters())
    handleResetLocations({})
  }

  return (
    <GridToolbarContainer>
      <Box display="flex" alignItems="flex-end" justifyContent="space-between" width="100%" p={2}>
        <Typography variant="h3" color={common.black}>
          {`${totalLocationsCount} ${totalLocationsCount === 1 ? T.LOCATION : T.LOCATIONS}`}
        </Typography>
        <Button onClick={handleAllLocationsClick} variant="text" sx={{ p: 0 }}>
          {T.ALL_LOCATIONS}
        </Button>
      </Box>
      <Divider />

      <Box px={2} pb={0} sx={{ width: '100%' }}>
        <Box mb={2} display="flex" alignItems="center" gap={1}>
          {/* Chrome/Firefox ignore autoComplete="off" on text input; "new-password" prevents autofill (Bugzilla #1119063) */}
          <GridToolbarQuickFilter
            sx={{ flex: 1 }}
            variant="outlined"
            placeholder={T.SEARCH_LOCATIONS}
            size="small"
            slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
          />

          <IconButton size="small" onClick={onFilterButtonClick}>
            <StyledBadge badgeContent={activeFiltersCount} color="primary">
              <FilterListIcon color="primary" />
            </StyledBadge>
          </IconButton>
          {showAddLocationIcon && (
            <IconButton size="small" onClick={handleAddLocationClick} sx={{ color: 'primary.main' }}>
              <AddIcon />
            </IconButton>
          )}
        </Box>

        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'capitalize',
                  minHeight: '40px',
                },
              }}
            >
              <Tab value={ACTIVE} label={`${T.ACTIVE} (${activeLocations.length})`} />
              <Tab value={DEACTIVATED} label={`${T.DEACTIVATED} (${deactivatedLocations.length})`} />
            </Tabs>
          </Grid>
          <Grid item>
            <LocationSortPopover />

            <IconButton size="small" onClick={handleMoreActionsClick} sx={{ color: 'text.secondary' }}>
              <MoreVertIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
      {showDeactivateAllConfirmation && (
        <DeactivateAllConfirmation
          onConfirm={handleConfirmDeactivateAll}
          onCancel={handleCancelDeactivateAll}
          deactivatableLocationsCount={deactivatableLocations.length}
        />
      )}
    </GridToolbarContainer>
  )
}

LocationsDataGridHeader.propTypes = {
  customerDetails: PropTypes.object.isRequired,
  showAddLocationIcon: PropTypes.bool,
  onFilterButtonClick: PropTypes.func.isRequired,
  onLocationStatusChange: PropTypes.func,
  handleResetLocations: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
}

export default LocationsDataGridHeader
